import React, { useState, useEffect } from 'react';
import {
  Card,
  Form,
  Input,
  Button,
  Select,
  Radio,
  Space,
  message,
  Tabs,
  Row,
  Col,
  Typography,
  Divider,
  Tag,
  Alert,
  DatePicker,
} from 'antd';
import {
  SendOutlined,
  UserOutlined,
  TeamOutlined,
  GlobalOutlined,
  WarningOutlined,
  InfoCircleOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
} from '@ant-design/icons';
import notificationService, { NotificationPayload } from '../services/notificationService';
import usersService, { User } from '../services/usersService';
import dayjs from 'dayjs';

const { TextArea } = Input;
const { Option } = Select;
const { Title, Text } = Typography;

type NotificationType = 'warning' | 'policy_violation' | 'feature_update' | 'account_notice' | 'custom';
type NotificationCategory = 'info' | 'warning' | 'success' | 'error' | 'promotional';
type NotificationPriority = 'low' | 'normal' | 'high' | 'critical';
type NotificationMode = 'individual' | 'selected' | 'all';

const Notifications: React.FC = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState<User[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<NotificationMode>('individual');

  useEffect(() => {
    if (activeTab === 'selected') {
      loadUsers();
    }
  }, [activeTab]);

  const loadUsers = async () => {
    setUsersLoading(true);
    try {
      const response = await usersService.getUsers({ page: 1, limit: 1000 });
      setUsers(response.users || []);
    } catch (error: any) {
      message.error('Failed to load users');
    } finally {
      setUsersLoading(false);
    }
  };

  const handleSubmit = async (values: any) => {
    setLoading(true);
    try {
      const payload: NotificationPayload = {
        title: values.title,
        body: values.body,
        image: values.image || undefined,
        type: values.type || 'custom',
        category: values.category || 'info',
        priority: values.priority || 'normal',
        actionUrl: values.actionUrl || undefined,
        expiresAt: values.expiresAt ? dayjs(values.expiresAt).toISOString() : undefined,
      };

      let result;

      if (activeTab === 'individual') {
        if (!values.userId) {
          message.error('Please select a user');
          return;
        }
        payload.userId = values.userId;
        result = await notificationService.sendToUser(payload);
        message.success('Notification sent successfully');
      } else if (activeTab === 'selected') {
        if (!selectedUsers || selectedUsers.length === 0) {
          message.error('Please select at least one user');
          return;
        }
        payload.userIds = selectedUsers;
        result = await notificationService.sendToMultipleUsers(payload);
        message.success(`Notification sent to ${result.successful || 0} users`);
        if (result.failed && result.failed > 0) {
          message.warning(`${result.failed} notifications failed to send`);
        }
      } else {
        // Broadcast to all users
        result = await notificationService.sendBroadcast(payload);
        message.success('Broadcast notification sent successfully');
      }

      form.resetFields();
      setSelectedUsers([]);
    } catch (error: any) {
      message.error(error.message || 'Failed to send notification');
    } finally {
      setLoading(false);
    }
  };

  const getTypeIcon = (type: NotificationType) => {
    switch (type) {
      case 'warning':
        return <WarningOutlined style={{ color: '#faad14' }} />;
      case 'policy_violation':
        return <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />;
      case 'feature_update':
        return <InfoCircleOutlined style={{ color: '#1890ff' }} />;
      case 'account_notice':
        return <CheckCircleOutlined style={{ color: '#52c41a' }} />;
      default:
        return <InfoCircleOutlined />;
    }
  };

  const getCategoryColor = (category: NotificationCategory) => {
    switch (category) {
      case 'warning':
        return 'orange';
      case 'error':
        return 'red';
      case 'success':
        return 'green';
      case 'promotional':
        return 'purple';
      default:
        return 'blue';
    }
  };

  const tabItems = [
    {
      key: 'individual',
      label: (
        <span>
          <UserOutlined /> Individual User
        </span>
      ),
    },
    {
      key: 'selected',
      label: (
        <span>
          <TeamOutlined /> Selected Users
        </span>
      ),
    },
    {
      key: 'all',
      label: (
        <span>
          <GlobalOutlined /> All Users (Broadcast)
        </span>
      ),
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <Title level={2}>Send Notifications</Title>
      <Text type="secondary">Send notifications to individual users, selected users, or broadcast to all users</Text>
      
      <Divider />

      <Card>
        <Tabs
          activeKey={activeTab}
          onChange={(key) => {
            setActiveTab(key as NotificationMode);
            form.resetFields();
            setSelectedUsers([]);
          }}
          items={tabItems}
        />

        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          style={{ marginTop: 24 }}
        >
          {activeTab === 'individual' && (
            <Form.Item
              name="userId"
              label="Select User"
              rules={[{ required: true, message: 'Please select a user' }]}
            >
              <Select
                showSearch
                placeholder="Search and select a user"
                optionFilterProp="children"
                loading={usersLoading}
                onFocus={loadUsers}
                filterOption={(input, option) =>
                  (option?.children as unknown as string)?.toLowerCase().includes(input.toLowerCase())
                }
              >
                {users.map((user) => (
                  <Option key={user._id} value={user._id}>
                    {user.name || user.username} ({user.email || user.phone || 'N/A'})
                  </Option>
                ))}
              </Select>
            </Form.Item>
          )}

          {activeTab === 'selected' && (
            <Form.Item
              label="Select Users"
              rules={[{ required: true, message: 'Please select at least one user' }]}
            >
              <Select
                mode="multiple"
                showSearch
                placeholder="Search and select users"
                value={selectedUsers}
                onChange={setSelectedUsers}
                loading={usersLoading}
                onFocus={loadUsers}
                filterOption={(input, option) =>
                  (option?.children as unknown as string)?.toLowerCase().includes(input.toLowerCase())
                }
                style={{ width: '100%' }}
              >
                {users.map((user) => (
                  <Option key={user._id} value={user._id}>
                    {user.name || user.username} ({user.email || user.phone || 'N/A'})
                  </Option>
                ))}
              </Select>
              <Text type="secondary" style={{ marginTop: 8, display: 'block' }}>
                {selectedUsers.length} user(s) selected
              </Text>
            </Form.Item>
          )}

          {activeTab === 'all' && (
            <Alert
              message="Broadcast Notification"
              description="This notification will be sent to all active users in the system. Use this feature carefully."
              type="warning"
              showIcon
              style={{ marginBottom: 24 }}
            />
          )}

          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                name="type"
                label="Notification Type"
                initialValue="custom"
              >
                <Select>
                  <Option value="custom">Custom</Option>
                  <Option value="warning">Warning</Option>
                  <Option value="policy_violation">Policy Violation</Option>
                  <Option value="feature_update">Feature Update</Option>
                  <Option value="account_notice">Account Notice</Option>
                  <Option value="admin">Admin Announcement</Option>
                  <Option value="system">System</Option>
                </Select>
              </Form.Item>
            </Col>

            <Col xs={24} md={12}>
              <Form.Item
                name="category"
                label="Category"
                initialValue="info"
              >
                <Select>
                  <Option value="info">Info</Option>
                  <Option value="warning">Warning</Option>
                  <Option value="success">Success</Option>
                  <Option value="error">Error</Option>
                  <Option value="promotional">Promotional</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                name="priority"
                label="Priority"
                initialValue="normal"
              >
                <Select>
                  <Option value="low">Low</Option>
                  <Option value="normal">Normal</Option>
                  <Option value="high">High</Option>
                  <Option value="critical">Critical</Option>
                </Select>
              </Form.Item>
            </Col>

            <Col xs={24} md={12}>
              <Form.Item
                name="expiresAt"
                label="Expiry Date (Optional)"
              >
                <DatePicker
                  showTime
                  style={{ width: '100%' }}
                  format="YYYY-MM-DD HH:mm:ss"
                  disabledDate={(current) => current && current < dayjs().startOf('day')}
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="title"
            label="Title"
            rules={[{ required: true, message: 'Please enter notification title' }]}
          >
            <Input placeholder="Enter notification title" maxLength={100} showCount />
          </Form.Item>

          <Form.Item
            name="body"
            label="Message"
            rules={[{ required: true, message: 'Please enter notification message' }]}
          >
            <TextArea
              rows={6}
              placeholder="Enter notification message"
              maxLength={500}
              showCount
            />
          </Form.Item>

          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                name="image"
                label="Image URL (Optional)"
              >
                <Input placeholder="https://example.com/image.jpg" />
              </Form.Item>
            </Col>

            <Col xs={24} md={12}>
              <Form.Item
                name="actionUrl"
                label="Action URL (Optional)"
              >
                <Input placeholder="/posts/123 or https://example.com" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item>
            <Space>
              <Button
                type="primary"
                htmlType="submit"
                icon={<SendOutlined />}
                loading={loading}
                size="large"
              >
                {activeTab === 'individual' && 'Send to User'}
                {activeTab === 'selected' && `Send to ${selectedUsers.length} Users`}
                {activeTab === 'all' && 'Broadcast to All Users'}
              </Button>
              <Button onClick={() => {
                form.resetFields();
                setSelectedUsers([]);
              }}>
                Reset
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Card>

      <Card style={{ marginTop: 24 }}>
        <Title level={4}>Notification Types Guide</Title>
        <Space direction="vertical" style={{ width: '100%' }}>
          <div>
            <Tag icon={<WarningOutlined />} color="orange">Warning</Tag>
            <Text>For warnings about account activity or behavior</Text>
          </div>
          <div>
            <Tag icon={<ExclamationCircleOutlined />} color="red">Policy Violation</Tag>
            <Text>For policy violations and terms of service issues</Text>
          </div>
          <div>
            <Tag icon={<InfoCircleOutlined />} color="blue">Feature Update</Tag>
            <Text>For new features, updates, and improvements</Text>
          </div>
          <div>
            <Tag icon={<CheckCircleOutlined />} color="green">Account Notice</Tag>
            <Text>For account-related notices and confirmations</Text>
          </div>
        </Space>
      </Card>
    </div>
  );
};

export default Notifications;

